import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Archive, AlertTriangle, Trash2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import AdminPageHeader from "./AdminPageHeader";

const HARD_DELETE_PHRASE = "I UNDERSTAND THIS IS IRREVERSIBLE";

export default function RetentionAdmin() {
  const { user } = useAuth();
  const isAdmin = user?.role === "hr" && user?.is_admin === true;
  const readOnly = !isAdmin;

  const [policy, setPolicy] = useState(null);
  const [edits, setEdits] = useState({});
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [running, setRunning] = useState(false);
  const [eligible, setEligible] = useState({ count: 0, items: [] });
  const [confirmText, setConfirmText] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  const reload = async () => {
    try {
      const [polRes, queueRes] = await Promise.all([
        api.get("/admin/retention"),
        api.get("/admin/retention/delete-eligible"),
      ]);
      setPolicy(polRes.data);
      setEligible(queueRes.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  useEffect(() => {
    reload();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const savePolicy = async () => {
    setSavingPolicy(true);
    try {
      const body = {};
      ["closed_archive_months", "archived_delete_eligible_years"].forEach((key) => {
        if (edits[key] !== undefined && edits[key] !== "") {
          body[key] = Number(edits[key]);
        }
      });
      ["active_indefinite", "legal_hold_immutable"].forEach((key) => {
        if (edits[key] !== undefined) body[key] = edits[key];
      });
      const res = await api.patch("/admin/retention", body);
      setPolicy(res.data);
      setEdits({});
      toast.success("Retention policy updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSavingPolicy(false);
    }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const res = await api.post("/admin/retention/run-now");
      toast.success(
        `Archived ${res.data.archived} · Skipped (legal hold) ${res.data.skipped_legal_hold} · Delete-eligible flagged ${res.data.delete_eligible_flagged}`,
      );
      await reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setRunning(false);
    }
  };

  const hardDelete = async (caseId) => {
    if (confirmText !== HARD_DELETE_PHRASE) {
      toast.error(`Type the exact phrase to confirm: ${HARD_DELETE_PHRASE}`);
      return;
    }
    setDeletingId(caseId);
    try {
      await api.post(`/admin/retention/hard-delete/${caseId}`, {
        confirm: HARD_DELETE_PHRASE,
      });
      toast.success("Case hard-deleted (tombstone audit row written)");
      setConfirmText("");
      await reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setDeletingId(null);
    }
  };

  if (!policy) {
    return (
      <div data-testid="retention-admin-page" className="space-y-3 p-1">
        <div className="skeleton-shimmer h-6 w-40" />
        <div className="skeleton-shimmer h-32 w-full" />
        <div className="skeleton-shimmer h-48 w-full" />
      </div>
    );
  }

  const v = (key) => (edits[key] !== undefined ? edits[key] : policy[key]);

  return (
    <div data-testid="retention-admin-page" className="space-y-6">
      <AdminPageHeader
        eyebrow="Admin · Retention"
        title="Retention"
        description="Archive cadence + delete-eligibility queue + hard-delete review. Legal-hold cases are excluded from every cycle."
        rightSlot={
          <Button
            onClick={runNow}
            disabled={readOnly || running}
            data-testid="retention-run-now"
            className="goodwin-primary-btn h-10 rounded-lg text-sm font-semibold text-white"
          >
            <RefreshCw
              className={`mr-1 h-4 w-4 ${running ? "animate-spin" : ""}`}
              strokeWidth={1.8}
            />
            {running ? "Running…" : "Run sweep now"}
          </Button>
        }
      />

      <div className="brand-card p-6" data-testid="retention-policy-card">
        <h2 className="text-base font-semibold text-[color:var(--page-fg)]">Policy</h2>
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="closed_archive_months" className="text-xs text-[color:var(--page-fg-muted)]">
              Closed cases archived after (months)
            </Label>
            <Input
              id="closed_archive_months"
              data-testid="retention-archive-months"
              type="number"
              min={0}
              disabled={readOnly}
              value={v("closed_archive_months")}
              onChange={(e) =>
                setEdits((s) => ({ ...s, closed_archive_months: e.target.value }))
              }
            />
            <p className="text-[11px] text-[color:var(--page-fg-muted)]">
              Set to 0 only for testing. 12 months is the recommended default.
            </p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="delete_years" className="text-xs text-[color:var(--page-fg-muted)]">
              Delete-eligible after (years archived)
            </Label>
            <Input
              id="delete_years"
              data-testid="retention-delete-years"
              type="number"
              min={1}
              disabled={readOnly}
              value={v("archived_delete_eligible_years")}
              onChange={(e) =>
                setEdits((s) => ({ ...s, archived_delete_eligible_years: e.target.value }))
              }
            />
            <p className="text-[11px] text-[color:var(--page-fg-muted)]">
              Cases are FLAGGED only — actual delete is always manual.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Switch
              id="active_indefinite"
              data-testid="retention-active-indefinite"
              checked={!!v("active_indefinite")}
              disabled={readOnly}
              onCheckedChange={(checked) =>
                setEdits((s) => ({ ...s, active_indefinite: checked }))
              }
            />
            <Label htmlFor="active_indefinite" className="text-sm text-[color:var(--page-fg-secondary)]">
              Open cases never auto-archive
            </Label>
          </div>
          <div className="flex items-center gap-3">
            <Switch
              id="legal_hold_immutable"
              data-testid="retention-legal-hold-immutable"
              checked={!!v("legal_hold_immutable")}
              disabled={readOnly}
              onCheckedChange={(checked) =>
                setEdits((s) => ({ ...s, legal_hold_immutable: checked }))
              }
            />
            <Label htmlFor="legal_hold_immutable" className="text-sm text-[color:var(--page-fg-secondary)]">
              Legal hold cases are immutable (recommended)
            </Label>
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between border-t border-[color:var(--border-subtle)] pt-4">
          <div className="text-xs text-[color:var(--page-fg-muted)]">
            Last archive run:{" "}
            <span className="font-mono text-[color:var(--page-fg-secondary)]">
              {policy.last_archive_run_at
                ? new Date(policy.last_archive_run_at).toLocaleString()
                : "never"}
            </span>
            {policy.last_archive_run_at && (
              <>
                {" · "}archived <b>{policy.last_archive_run_count}</b>
                {" · "}skipped legal hold <b>{policy.last_archive_run_skipped_legal_hold}</b>
              </>
            )}
          </div>
          {!readOnly && Object.keys(edits).length > 0 && (
            <Button
              data-testid="retention-save-policy"
              onClick={savePolicy}
              disabled={savingPolicy}
              className="goodwin-primary-btn text-white"
            >
              {savingPolicy ? "Saving…" : "Save policy"}
            </Button>
          )}
        </div>
      </div>

      <div className="brand-card p-6" data-testid="retention-delete-queue-card">
        <div className="flex items-start gap-3">
          <span
            className="mt-1 flex h-9 w-9 items-center justify-center rounded-xl"
            style={{ backgroundColor: "rgba(229,77,77,0.14)", color: "#C03030" }}
          >
            <AlertTriangle className="h-4 w-4" />
          </span>
          <div className="flex-1">
            <h2 className="text-base font-semibold text-[color:var(--page-fg)]">
              Delete-eligible queue ({eligible.count})
            </h2>
            <p className="mt-1 text-xs text-[color:var(--page-fg-muted)]">
              These cases passed the delete-eligibility threshold AND are not on legal hold.
              Hard delete is irreversible: the case row, documents, and storage blobs are
              removed but a tombstone audit entry preserves the metadata.
            </p>
          </div>
        </div>
        {eligible.count === 0 ? (
          <p className="mt-4 text-sm text-[color:var(--page-fg-muted)]">No delete-eligible cases.</p>
        ) : (
          <div className="mt-4 space-y-3">
            <div className="rounded-xl bg-[color:var(--surface-2)] p-3 text-xs text-[color:var(--page-fg-secondary)]">
              Type <code className="rounded bg-white px-1.5 py-0.5">{HARD_DELETE_PHRASE}</code> to enable the delete buttons:
            </div>
            <Input
              data-testid="retention-confirm-input"
              placeholder={HARD_DELETE_PHRASE}
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              disabled={readOnly}
            />
            <div className="overflow-hidden rounded-xl border border-[color:var(--border-subtle)]">
              <table className="w-full text-sm">
                <thead className="bg-[color:var(--surface-2)] text-left text-xs uppercase tracking-wide text-[color:var(--page-fg-muted)]">
                  <tr>
                    <th className="px-4 py-2">Case</th>
                    <th className="px-4 py-2">Issue</th>
                    <th className="px-4 py-2">Closed</th>
                    <th className="px-4 py-2">Archived</th>
                    <th className="px-4 py-2 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {eligible.items.map((c) => (
                    <tr key={c.id} className="border-t border-[color:var(--border-subtle)]">
                      <td className="px-4 py-2 font-mono text-[color:var(--page-fg)]">
                        {c.case_id}
                      </td>
                      <td className="px-4 py-2 text-[color:var(--page-fg-secondary)]">{c.issue_type}</td>
                      <td className="px-4 py-2 text-xs text-[color:var(--page-fg-muted)]">
                        {c.closed_at?.slice(0, 10) || "—"}
                      </td>
                      <td className="px-4 py-2 text-xs text-[color:var(--page-fg-muted)]">
                        {c.archived_at?.slice(0, 10) || "—"}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <Button
                          data-testid={`retention-hard-delete-${c.id}`}
                          variant="outline"
                          size="sm"
                          disabled={
                            readOnly ||
                            confirmText !== HARD_DELETE_PHRASE ||
                            deletingId === c.id
                          }
                          onClick={() => hardDelete(c.id)}
                          className="border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800"
                        >
                          <Trash2 className="mr-1 h-3.5 w-3.5" />
                          {deletingId === c.id ? "Deleting…" : "Hard delete"}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
